import request from '@/utils/requests';
import companies from '@/store/modules/admin/company/companies';
import categories from '@/store/modules/admin/categories';
import subscriptionPlans from '@/store/modules/admin/subscription-plans';

const getters = {
  fetchStatus: (state) => state.status,
  getTempAccounts: (state) => state.tempAccounts,
  getOfferings: (state) => state.offerings,
  getMaterialGroups: (state) => state.materialGroups,
  getTempAccountsStats: (state) => state.tempAccountsStats,
  getRecommendationsDisplayedCount: (state) => state.recommendationsDisplayedCount,
  getDonationTags: (state) => state.donationTags,
  getDonationTagSubcategories: (state) => state.donationTagSubcategories,
};
const mutations = {
  fetchRequest: (state) => {
    state.status = 'loading';
  },
  fetchSuccess: (state) => {
    state.status = 'success';
  },
  setTempAccounts: (state, tempAccounts) => {
    state.tempAccounts = tempAccounts;
  },
  fetchError: (state) => {
    state.status = 'error';
  },
  setStatus: (state, status) => {
    state.status = status;
  },
  setOfferings: (state, offerings) => {
    state.offerings = offerings;
  },
  setMaterialGroups: (state, materialGroups) => {
    state.materialGroups = materialGroups;
  },
  setTempAccountsStats: (state, tempAccountsStats) => {
    state.tempAccountsStats = tempAccountsStats;
  },
  setRecommendationsDisplayedCount: (state, recommendationsDisplayedCount) => {
    state.recommendationsDisplayedCount = recommendationsDisplayedCount;
  },
  setDonationTags: (state, donationTags) => {
    state.donationTags = donationTags;
  },
  setDonationTagSubcategories: (state, donationTagSubcategories) => {
    state.donationTagSubcategories = donationTagSubcategories;
  },
};
const actions = {
  fetchTempAccounts: async ({ commit, rootGetters }) => {
    try {
      commit('setTempAccounts', []);
      commit('fetchRequest');
      const response = await request(
        'GET',
        'admin/temporary-accounts',
        rootGetters['auth/getToken'],
        {},
        {}
      );
      setTimeout(commit('setTempAccounts', response.data), 250);
      commit('fetchSuccess');
      return Promise.resolve();
    } catch (e) {
      console.log(e);
      commit('fetchError');
      return Promise.reject();
    }
  },
  reviewTempAccount: async ({ commit, rootGetters }, payload) => {
    try {
      commit('setStatus', 'loading');
      const response = await request(
        'POST',
        'admin/temporary-accounts/review',
        rootGetters['auth/getToken'],
        payload,
        {}
      );
      commit('setStatus', 'success');
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      commit('setStatus', 'error');
      return Promise.reject(e);
    }
  },
  fetchOfferings: async ({ commit, rootGetters }, payload) => {
    try {
      commit('setOfferings', {});
      const response = await request(
        'GET',
        'admin/materials',
        rootGetters['auth/getToken'],
        {},
        payload.params
      );
      commit('setOfferings', response.data);
      return Promise.resolve();
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchMaterialGroups: async ({ commit, rootGetters }) => {
    try {
      commit('fetchRequest');
      const response = await request(
        'GET',
        'admin/material-groups',
        rootGetters['auth/getToken'],
        {},
        {}
      );
      commit('setMaterialGroups', response.data);
      commit('fetchSuccess');
      return Promise.resolve();
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchSynergiesStats: async ({ rootGetters }, payload) => {
    try {
      const response = await request(
        'GET',
        'admin/exchanges/stats',
        rootGetters['auth/getToken'],
        {},
        payload.params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchMaterialsStats: async ({ rootGetters }) => {
    try {
      const response = await request('GET', 'admin/materials/stats', rootGetters['auth/getToken']);
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchTempAccountsStats: async ({ rootGetters }) => {
    try {
      const response = await request(
        'GET',
        'admin/temporary-accounts/stats',
        rootGetters['auth/getToken']
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchTempAccountsWithinTimePeriod: async ({ rootGetters }, { startDate, endDate }) => {
    try {
      const queryParams = {};
      if (startDate) {
        queryParams.start_date = startDate;
      }
      if (endDate) {
        queryParams.end_date = endDate;
      }
      const response = await request(
        'GET',
        'admin/temporary-accounts/stats',
        rootGetters['auth/getToken'],
        {},
        queryParams
      );
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  fetchRecommendationsDisplayedCount: async ({ commit, rootGetters }) => {
    try {
      commit('setRecommendationsDisplayedCount', null);
      const response = await request(
        'GET',
        'admin/total-recommendations-clicks',
        rootGetters['auth/getToken']
      );
      setTimeout(commit('setRecommendationsDisplayedCount', response.data.total_clicks), 250);
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  // Donation Tags Actions
  fetchDonationTags: async ({ commit, rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/donation-tags',
        rootGetters['auth/getToken'],
        {},
        params
      );
      // Store donation tags in state for use in subcategories
      const tags = response.data?.items || response.data || [];
      commit('setDonationTags', Array.isArray(tags) ? tags : []);
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  createDonationTag: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/donation-tags',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  updateDonationTag: async ({ rootGetters }, { id, data }) => {
    try {
      const response = await request(
        'PUT',
        `admin/donation-tags/${id}`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  deleteDonationTag: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'DELETE',
        `admin/donation-tags/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  // Donation Tag Subcategories Actions
  fetchDonationTagSubcategories: async ({ commit, rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/donation-tag-subcategories',
        rootGetters['auth/getToken'],
        {},
        params
      );
      const subcategories = response.data?.items || response.data || [];
      commit('setDonationTagSubcategories', Array.isArray(subcategories) ? subcategories : []);
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  createDonationTagSubcategory: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/donation-tag-subcategories',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  updateDonationTagSubcategory: async ({ rootGetters }, { id, data }) => {
    try {
      const response = await request(
        'PUT',
        `admin/donation-tag-subcategories/${id}`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  deleteDonationTagSubcategory: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'DELETE',
        `admin/donation-tag-subcategories/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  // Drivers Actions
  fetchDrivers: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/drivers',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  createDriver: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/drivers',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  updateDriver: async ({ rootGetters }, { id, data }) => {
    try {
      const response = await request(
        'PUT',
        `admin/drivers/${id}`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  deleteDriver: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'DELETE',
        `admin/drivers/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  // Vehicles Actions
  fetchVehicles: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/vehicles',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  createVehicle: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/vehicles',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  updateVehicle: async ({ rootGetters }, { id, data }) => {
    try {
      const response = await request(
        'PUT',
        `admin/vehicles/${id}`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  deleteVehicle: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'DELETE',
        `admin/vehicles/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  // Inventory Values Actions
  fetchInventoryValues: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/inventory-values',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  createInventoryValue: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/inventory-values',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  updateInventoryValue: async ({ rootGetters }, { id, data }) => {
    try {
      const response = await request(
        'PUT',
        `admin/inventory-values/${id}`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  deleteInventoryValue: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'DELETE',
        `admin/inventory-values/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchCompaniesForInventoryValue: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/companies',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },

  // Marketplace Locations Actions
  fetchMarketplaceLocations: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'api/marketplace-locations',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  createMarketplaceLocation: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/marketplace-locations',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  updateMarketplaceLocation: async ({ rootGetters }, { id, data }) => {
    try {
      const response = await request(
        'PUT',
        `admin/marketplace-locations/${id}`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  deleteMarketplaceLocation: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'DELETE',
        `admin/marketplace-locations/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },

  // Outreach Partners Actions
  fetchOutreachPartners: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'api/outreach-partners',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  createOutreachPartner: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/outreach-partners',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  updateOutreachPartner: async ({ rootGetters }, { id, data }) => {
    try {
      const response = await request(
        'PUT',
        `admin/outreach-partners/${id}`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  deleteOutreachPartner: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'DELETE',
        `admin/outreach-partners/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },

  // Beneficiary Types Actions
  fetchBeneficiaryTypes: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'api/beneficiary-types',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  createBeneficiaryType: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/beneficiary-types',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  updateBeneficiaryType: async ({ rootGetters }, { id, data }) => {
    try {
      const response = await request(
        'PUT',
        `admin/beneficiary-types/${id}`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  deleteBeneficiaryType: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'DELETE',
        `admin/beneficiary-types/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },

  // Volunteers Actions
  fetchVolunteers: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'api/volunteers',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  createVolunteer: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/volunteers',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  updateVolunteer: async ({ rootGetters }, { id, data }) => {
    try {
      const response = await request(
        'PUT',
        `admin/volunteers/${id}`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  deleteVolunteer: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'DELETE',
        `admin/volunteers/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  importVolunteersCsv: async ({ rootGetters }, formData) => {
    try {
      const response = await request(
        'POST',
        'admin/volunteers/import',
        rootGetters['auth/getToken'],
        formData,
        {},
        { 'Content-Type': 'multipart/form-data' }
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },

  // Helper action for addresses (used in marketplace locations)
  // Fetches companies and extracts their addresses
  fetchAddresses: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/companies',
        rootGetters['auth/getToken'],
        {},
        { ...params, limit: 1000 } // Get all companies to extract addresses
      );
      // Extract addresses from companies
      const addresses = [];
      if (response.data && response.data.items) {
        response.data.items.forEach(company => {
          if (company.addresses && Array.isArray(company.addresses)) {
            addresses.push(...company.addresses);
          }
        });
      }
      return Promise.resolve({ data: addresses });
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },

  // Donor Metadata Actions
  fetchDonorMetadataList: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/donor-metadata',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  upsertDonorMetadata: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/donor-metadata/upsert',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },

  // Donation Metadata Actions (for QR scanning and allocation)
  fetchDonationMetadataByMaterialId: async ({ rootGetters }, materialId) => {
    try {
      const response = await request(
        'GET',
        `admin/donation-metadata/${materialId}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchDonationMetadataList: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/donation-metadata',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },

  // Donation Allocation Actions
  createDonationAllocation: async ({ rootGetters }, { materialId, data }) => {
    try {
      const response = await request(
        'POST',
        `admin/donation-metadata/${materialId}/allocate`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchDonationAllocations: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/donation-allocations',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchDonationAllocationById: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'GET',
        `admin/donation-allocations/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  updateDonationAllocation: async ({ rootGetters }, { id, data }) => {
    try {
      const response = await request(
        'PUT',
        `admin/donation-allocations/${id}`,
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  deleteDonationAllocation: async ({ rootGetters }, id) => {
    try {
      const response = await request(
        'DELETE',
        `admin/donation-allocations/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  returnRemainingItems: async ({ rootGetters }, allocationId) => {
    try {
      const response = await request(
        'POST',
        `admin/donation-allocations/${allocationId}/return-remaining`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response.data || response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchEventAllocations: async ({ rootGetters }, eventId) => {
    try {
      const response = await request(
        'GET',
        `admin/marketplace-events/${eventId}/allocations`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  fetchGroupedEventAllocations: async ({ rootGetters }, eventId) => {
    try {
      const response = await request(
        'GET',
        `admin/marketplace-events/${eventId}/allocations/grouped`,
        rootGetters['auth/getToken'],
        {},
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },
  createBatchDonationAllocation: async ({ rootGetters }, data) => {
    try {
      const response = await request(
        'POST',
        'admin/donation-allocations/batch',
        rootGetters['auth/getToken'],
        data,
        {}
      );
      return Promise.resolve(response.data);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },

  // Marketplace Events Actions
  fetchMarketplaceEvents: async ({ rootGetters }, params = {}) => {
    try {
      const response = await request(
        'GET',
        'admin/marketplace-events',
        rootGetters['auth/getToken'],
        {},
        params
      );
      return Promise.resolve(response);
    } catch (e) {
      console.log(e);
      return Promise.reject(e);
    }
  },

};
const modules = { companies, categories, 'subscription-plans': subscriptionPlans };
const state = {
  tempAccounts: {},
  status: '',
  offerings: {},
  materialGroups: {},
  tempAccountsStats: {},
  donationTags: [],
  donationTagSubcategories: [],
};

export default {
  namespaced: true,
  getters,
  mutations,
  actions,
  modules,
  state,
};
